import { useState, useEffect, useRef } from "react";
import { useDebounce } from "../hooks/useDebounce";
import { HiOutlineX, HiOutlineChevronDown } from "react-icons/hi";
import { fetchMasterData } from "../services/masterDataApi";

const VARIANT_CLS = {
  report: "h-[34px] border border-[#d0d5dd] rounded-lg text-[12.5px] bg-white focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4]/20 transition-all w-full",
  form: "w-full py-2.5 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10",
};

// fetchFn contract: (search, page) => Promise<{ data: [...], total_count: number }>
// A server-paginated fetchFn MUST forward the `page` argument — the "N more…" row
// requests page 2, 3, … through it. A fetchFn that hardcodes page 1 keeps returning
// the same first page, so options past the first page become unreachable.

// id -> label, namespaced by cacheNamespace (defaults to placeholder — fine
// as long as no two distinct dropdowns share both a placeholder AND an id
// space; pass an explicit cacheNamespace, e.g. "district_id", wherever that
// could actually collide). Survives a remount of the SearchableDropdown
// instance (e.g. one triggered by a sibling filter changing), so a value
// that was already resolved once never silently reverts to a blank display
// just because a *later* fetchFn call — now scoped by a changed sibling
// filter (like cycle_type) — no longer returns that item in its result set.
const labelCache = new Map();
const cacheKey = (namespace, id) => `${namespace}::${id}`;

// Wraps an already-loaded static list (e.g. states) into a fetchFn-compatible
// function, so a small in-memory list can use SearchableDropdown exactly like
// a server-paginated one (matching search-as-you-type UX) without network calls.
// Static lists are returned whole, so there is never a second page to request.
export function staticFetchFn(list) {
  return (search) => {
    const items = search
      ? list.filter((item) => (item.name || "").toLowerCase().includes(search.toLowerCase()))
      : list;
    return Promise.resolve({ data: items, total_count: items.length });
  };
}

// GET /commons/list?type=states is not role-scoped server-side, so an
// unrestricted caller (ADMIN) searches it live; a role whose visible states
// are already a small, pre-fetched, real assignment list (SPL/PL/PM) just
// filters that list client-side via staticFetchFn — no pagination-cap issue
// since that list is never large.
export function scopedStatesFetchFn(isUnrestricted, scopedList) {
  return isUnrestricted
    ? (search, page = 1) => fetchMasterData("states", { page, page_size: 20, search })
    : staticFetchFn(scopedList);
}

function SearchableDropdown({
  value,
  onChange,
  placeholder = "Search...",
  fetchFn,
  disabled = false,
  variant = "report",
  emptyMessage,
  cacheNamespace = placeholder,
  // Optional: (id) => Promise<{id, name} | null> — resolves a pre-selected
  // value's label directly by id. Without this, label resolution falls back
  // to fetchFn("", 1) and only checks whatever the unsearched first page
  // happens to contain — fine for a short list, but on a large one (hundreds+
  // rows) the selected item is often just not on that page, so the field
  // shows blank until the user searches for it themselves. Pass this
  // wherever that's a real possibility.
  resolveFn,
}) {
  const [query, setQuery] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");
  const [options, setOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [moreCount, setMoreCount] = useState(0);
  const [fetchedPage, setFetchedPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const optionRefs = useRef([]);
  const debouncedQuery = useDebounce(query, 400);

  // Reset pagination when search query changes
  useEffect(() => {
    setHasMore(false);
    setMoreCount(0);
    setFetchedPage(1);
  }, [debouncedQuery]);

  // Fetch options whenever dropdown opens or search query changes
  useEffect(() => {
    if (disabled || !fetchFn || !isOpen) return;
    setLoading(true);
    fetchFn(debouncedQuery, 1)
      .then((res) => {
        const items = res.data || [];
        setOptions(items);
        setActiveIndex(items.length ? 0 : -1);
        const total = res.total_count || 0;
        setHasMore(items.length < total);
        setMoreCount(total - items.length);
        setFetchedPage(1);
        items.forEach((item) => labelCache.set(cacheKey(cacheNamespace, item.id), item.name));
        if (value && !selectedLabel) {
          const found = items.find((item) => String(item.id) === String(value));
          if (found) setSelectedLabel(found.name);
        }
      })
      .catch(() => { setOptions([]); setActiveIndex(-1); setHasMore(false); })
      .finally(() => setLoading(false));
  }, [debouncedQuery, disabled, isOpen]);

  useEffect(() => {
    if (!value) {
      setSelectedLabel("");
      setQuery("");
      setOptions([]);
      setHasMore(false);
    }
  }, [value]);

  // Resolve the label for a preselected value — after a remount the closed
  // input would otherwise show the placeholder until the dropdown is opened.
  // Check the cross-remount label cache first: a value resolved once (by a
  // prior fetch, or by the user picking it) keeps its label even if a later
  // fetchFn call — now scoped by a changed sibling filter such as cycle_type —
  // would no longer return that item in its result set at all.
  useEffect(() => {
    if (!value || selectedLabel) return;
    const cached = labelCache.get(cacheKey(cacheNamespace, value));
    if (cached) {
      setSelectedLabel(cached);
      return;
    }
    let cancelled = false;
    if (resolveFn) {
      resolveFn(value)
        .then((found) => {
          if (cancelled || !found) return;
          labelCache.set(cacheKey(cacheNamespace, found.id), found.name);
          setSelectedLabel(found.name);
        })
        .catch(() => {});
      return () => { cancelled = true; };
    }
    if (!fetchFn) return;
    fetchFn("", 1)
      .then((res) => {
        if (cancelled) return;
        const found = (res.data || []).find((item) => String(item.id) === String(value));
        if (found) {
          labelCache.set(cacheKey(cacheNamespace, found.id), found.name);
          setSelectedLabel(found.name);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selectedLabel, disabled]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keep the keyboard-highlighted option scrolled into view as it changes.
  useEffect(() => {
    if (activeIndex < 0) return;
    optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleFocus = () => {
    if (disabled) return;
    setIsOpen(true);
    setQuery("");
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setIsOpen(true);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (options.length === 0) return;
      setActiveIndex((prev) => (prev + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (options.length === 0) return;
      setActiveIndex((prev) => (prev - 1 + options.length) % options.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < options.length) {
        handleSelect(options[activeIndex]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setQuery("");
    }
  };

  const handleSelect = (item) => {
    labelCache.set(cacheKey(cacheNamespace, item.id), item.name);
    setSelectedLabel(item.name);
    setQuery("");
    setIsOpen(false);
    onChange(item.id);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedLabel("");
    setQuery("");
    setOptions([]);
    setIsOpen(false);
    setHasMore(false);
    onChange("");
  };

  const handleLoadMore = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const nextPage = fetchedPage + 1;
    setLoadingMore(true);
    // debouncedQuery, not query — the options already listed were fetched for the
    // debounced term, so the next page has to be fetched for that same term.
    fetchFn(debouncedQuery, nextPage)
      .then((res) => {
        const items = res.data || [];
        const seen = new Set(options.map((item) => String(item.id)));
        const fresh = items.filter((item) => !seen.has(String(item.id)));
        const total = res.total_count || 0;
        if (fresh.length === 0) {
          // Nothing new came back — either the list is genuinely exhausted or this
          // fetchFn ignored the page argument. Either way, stop offering more.
          setHasMore(false);
          setMoreCount(0);
          if (process.env.NODE_ENV === "development" && options.length < total) {
            console.warn(
              `SearchableDropdown: fetchFn returned no new options for page ${nextPage}. ` +
                "Does it forward its `page` argument?"
            );
          }
          return;
        }
        const newOptions = [...options, ...fresh];
        setOptions(newOptions);
        setFetchedPage(nextPage);
        setHasMore(newOptions.length < total);
        setMoreCount(Math.max(0, total - newOptions.length));
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const baseCls = VARIANT_CLS[variant] || VARIANT_CLS.report;
  const disabledCls = disabled ? " opacity-50 cursor-not-allowed bg-[#f6f8fa]" : "";
  const paddingCls = variant === "report" ? " pl-2.5 pr-7" : " pl-4 pr-8";

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        type="text"
        className={baseCls + disabledCls + paddingCls}
        value={isOpen ? query : selectedLabel}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={isOpen ? "Type to search..." : placeholder}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
      />
      {value && !disabled ? (
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8b949e] hover:text-[#57606a] bg-transparent border-none cursor-pointer p-0.5 leading-none"
          onMouseDown={handleClear}
        >
          <HiOutlineX className={variant === "report" ? "text-[10px]" : "text-xs"} />
        </button>
      ) : (
        <HiOutlineChevronDown
          className={`absolute right-2 top-1/2 -translate-y-1/2 text-[#8b949e] pointer-events-none transition-transform ${
            variant === "report" ? "text-[10px]" : "text-xs"
          } ${isOpen ? "rotate-180" : ""}`}
        />
      )}

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-[#d0d5dd] rounded-md shadow-lg z-[300] max-h-52 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-2 text-[12px] text-[#8b949e]">Loading…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-[12px] text-[#8b949e]">
              {emptyMessage || "No results found"}
            </div>
          ) : (
            <>
              {options.map((item, index) => (
                <div
                  key={item.id}
                  ref={(el) => { optionRefs.current[index] = el; }}
                  className={`px-3 py-2 text-[12.5px] cursor-pointer transition-colors ${
                    index === activeIndex
                      ? "bg-[#f3f4f6]"
                      : ""
                  } ${
                    String(item.id) === String(value)
                      ? "bg-[#eff6ff] text-[#0078d4] font-medium"
                      : "text-[#24292f]"
                  }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  {item.name}
                </div>
              ))}
              {hasMore && (
                <div
                  className="px-3 py-2 text-xs font-medium text-[#0078d4] cursor-pointer hover:bg-[#eff6ff] border-t border-[#f0f0f0]"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleLoadMore}
                >
                  {loadingMore ? "Loading…" : `${moreCount} more…`}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchableDropdown;
